import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { FormikErrors, useFormik } from 'formik'
import produce from 'immer'

import { useToastContext } from '../../../../../../../../contexts'
import {
  DeliveryForecast,
  DeliveryForecastItemSplit,
  PatchRequest,
  WebEdiDeliveryForecastDto,
} from '../../../../../../../../data/models'
import { getHandleableError } from '../../../../../../../../utils/errors/isXiaError'
import {
  ComparisonArray,
  areItemsModified,
} from '../../../../../../../../utils/helper/checkConfirmationCodes'
import {
  FieldToValidate,
  validateFormikFields,
} from '../../../../../../../../utils/helper/validateFormikFields'
import { useConfigContext } from '../../../../../../../ui-config'
import {
  requestPatchDeliveryForecastsById,
  requestPutSplitItem,
  useAllDeliveryForecastItemSplits,
  useDeliveryForecastItemSplitsById,
} from '../../../../../../data'
import { areItemSplitsModified } from '../../../../../../scripts'
import { EditSplitItemView } from './EditSplitItem.view'

interface Props {
  deliveryForecastId: string
  itemId: string
  setSelectedItems: React.Dispatch<
    React.SetStateAction<DeliveryForecastItemSplit[] | null | undefined>
  >
  rowData: DeliveryForecastItemSplit
}

export interface FormValues {
  confirmationDeliveryDate: DeliveryForecastItemSplit['ConfirmationDeliveryDate']
  confirmationDeliveryQuantity?: DeliveryForecastItemSplit['ConfirmationDeliveryQuantity']
}

export type FormFieldName = keyof FormValues

export const EditSplitItemContainer: React.FC<Props> = ({
  deliveryForecastId,
  itemId,
  setSelectedItems,
  rowData,
}) => {
  const { t, i18n } = useTranslation()
  const locale = i18n.language
  const { uiConfig: config } = useConfigContext()
  const [visible, setVisible] = useState(false)

  const { data: itemSplits } = useAllDeliveryForecastItemSplits(
    deliveryForecastId,
    itemId,
  )
  const { data } = useDeliveryForecastItemSplitsById(
    deliveryForecastId,
    itemId,
    rowData.Id!,
  )

  const { showToast } = useToastContext()
  const [isRequestRunning, setIsRequestRunning] = useState(false)
  const [status, setStatus] =
    useState<DeliveryForecastItemSplit['Status']>(undefined)

  useEffect(() => {
    if (data) {
      setStatus(data.Status)
    }
  }, [data])

  // ************************************************************
  // formik
  // ************************************************************
  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: {
      confirmationDeliveryDate: rowData.ConfirmationDeliveryDate,
      confirmationDeliveryQuantity: rowData.ConfirmationDeliveryQuantity,
    },
    validate: formData => {
      let errors: FormikErrors<FormValues> = {}

      const fieldsToValidate: FieldToValidate[] = [
        {
          // confirmationDeliveryDate
          fieldName: 'confirmationDeliveryDate',
          config:
            config.deliveryForecast.editSplitItem.confirmationDeliveryDate,
        },
        {
          // confirmationDeliveryQuantity
          fieldName: 'confirmationDeliveryQuantity',
          config:
            config.deliveryForecast.editSplitItem.confirmationDeliveryQuantity,
        },
      ]

      validateFormikFields(fieldsToValidate, formData, errors)

      if (
        formData.confirmationDeliveryQuantity === null ||
        formData.confirmationDeliveryQuantity === undefined
      ) {
        errors.confirmationDeliveryQuantity = t(
          'deliveryForecast.deliveryScheduleDetails.splits.table.edit.dialog.form.confirmationDeliveryQuantity.errorMessages.required',
        )
      } else if (formData.confirmationDeliveryQuantity <= 0) {
        errors.confirmationDeliveryQuantity = t(
          'deliveryForecast.deliveryScheduleDetails.splits.table.edit.dialog.form.confirmationDeliveryQuantity.errorMessages.numberTooSmall',
          { number: 0 },
        )
      }

      return errors
    },
    onSubmit: async formData => {
      // TODO: onsubmit überall gleich aufbauen
      // deactivate the buttons
      setIsRequestRunning(true)

      try {
        // close the dialog and reset
        handleClose()

        // submit form
        await submitForm(formData)

        // show success toast
        showToast({
          style: {},
          closable: true,
          severity: 'success',
          summary: t(
            'deliveryForecast.deliveryScheduleDetails.splits.table.edit.dialog.form.save.messages.success.summary',
          ),
          detail: t(
            'deliveryForecast.deliveryScheduleDetails.splits.table.edit.dialog.form.save.messages.success.detail',
            {
              deliveryDate: new Date(
                formData.confirmationDeliveryDate!,
              ).toLocaleDateString(locale),
            },
          ),
        })
      } catch (error) {
        let handleableError = getHandleableError(error)
        let errorMessage = handleableError.message

        // show error toast
        showToast({
          style: {},
          closable: true,
          severity: 'error',
          sticky: true,
          summary: t(
            'deliveryForecast.deliveryScheduleDetails.splits.table.edit.dialog.form.save.messages.failure.summary',
          ),
          detail: errorMessage,
        })
      } finally {
        setSelectedItems([]) // deselect the items in the table
        setIsRequestRunning(false)
      }
    },
  })

  const isFormFieldValid = (name: FormFieldName) =>
    !(formik.touched[name] && formik.errors[name])

  const getFormErrorMessage = (name: FormFieldName) => {
    return (
      !isFormFieldValid(name) && (
        <small className="p-error">{formik.errors[name]}</small>
      )
    )
  }

  // check if items are modified
  function getConfirmationCode(
    formData: FormValues,
  ): DeliveryForecastItemSplit['Status'] {
    if (data?.Status === 'ADDED') return 'ADDED'

    if (status === 'REJECTED') return 'REJECTED'

    const comparisionArray: ComparisonArray = [
      {
        order: new Date(data?.BuyerDeliveryDate!).toDateString(),
        confirmation: new Date(
          formData.confirmationDeliveryDate!,
        ).toDateString(),
      },
      {
        order: data?.BuyerDeliveryQuantity,
        confirmation: formData.confirmationDeliveryQuantity,
      },
    ]

    return areItemsModified(comparisionArray) ? 'MODIFIED' : 'NOT_SET'
  }

  // submit form
  const submitForm = async (formData: FormValues) => {
    const newSplit = produce(data!, draft => {
      draft.ConfirmationDeliveryDate = formData.confirmationDeliveryDate
      draft.ConfirmationDeliveryQuantity = formData.confirmationDeliveryQuantity
      draft.Status = getConfirmationCode(formData)
      draft.QuantityDeviation =
        rowData.BuyerDeliveryQuantity - formData.confirmationDeliveryQuantity!
    })

    // Set correct confirmation code
    const newDataArray: DeliveryForecastItemSplit[] =
      itemSplits?.map(item => (item.Id === rowData.Id ? newSplit : item)) || []

    const notSetCode: DeliveryForecast['ConfirmationCode'] = 'NOT_SET'
    const modifiedCode: DeliveryForecast['ConfirmationCode'] = 'MODIFIED'
    const patchData: PatchRequest<WebEdiDeliveryForecastDto<DeliveryForecast>> =
      [
        {
          op: 'replace',
          path: '/Payload/ConfirmationCode',
          value: areItemSplitsModified(newDataArray!)
            ? modifiedCode
            : notSetCode,
        },
      ]

    requestPatchDeliveryForecastsById(deliveryForecastId, patchData)

    await requestPutSplitItem(deliveryForecastId, itemId, rowData.Id!, newSplit)
  }

  // handleReset
  const handleReset = async () => {
    await formik.setFieldValue(
      'confirmationDeliveryDate',
      rowData.BuyerDeliveryDate,
    )
    await formik.setFieldValue(
      'confirmationDeliveryQuantity',
      rowData.BuyerDeliveryQuantity,
    )

    setStatus('NOT_SET')
  }

  const handleClose = () => {
    formik.resetForm()
    setStatus(data?.Status)
    setVisible(false)
  }

  return (
    <EditSplitItemView
      deliveryForecastId={deliveryForecastId}
      visible={visible}
      setVisible={setVisible}
      itemId={itemId}
      formik={formik}
      isFormFieldValid={isFormFieldValid}
      getConfirmationCode={getConfirmationCode}
      getFormErrorMessage={getFormErrorMessage}
      handleClose={handleClose}
      handleReset={handleReset}
      isRequestRunning={isRequestRunning}
      rowData={rowData}
    />
  )
}
